/**
 * Environment detection utilities for web applications
 */

// Environment types
export type Environment = "development" | "staging" | "production";

/**
 * Check if the current environment is development
 * Works in both browser and server environments
 */
export function isDevelopment(): boolean {
  // Browser environment checks
  if (typeof window !== "undefined") {
    return window.location.origin.includes("localhost:") || window.location.origin.includes("127.0.0.1:");
  }

  // Node environment checks
  if (typeof process !== "undefined") {
    return process.env.NODE_ENV === "development";
  }

  // Vite environment (if available)
  if (typeof import.meta !== "undefined" && "env" in import.meta) {
    const env = (import.meta as any).env;
    return env?.DEV === true;
  }

  return false;
}

/**
 * Check if the current environment is an AWS Amplify preview deployment
 */
export function isAmplifyPreview(): boolean {
  if (typeof window !== "undefined") {
    return window.location?.hostname?.endsWith(".amplifyapp.com") ?? false;
  }
  return false;
}

/**
 * Check if the current environment is staging
 * Works in both browser and server environments
 */
export function isStaging(): boolean {
  // Browser environment checks
  if (typeof window !== "undefined") {
    return (
      window.location.origin.includes("staging") ||
      window.location.origin.includes("github.io") || // GitHub Pages should use staging API
      isAmplifyPreview() // Amplify preview deployments should use staging API
    );
  }

  // Node environment checks
  if (typeof process !== "undefined") {
    return process.env.NODE_ENV === "staging" || process.env.STAGING === "true";
  }

  return false;
}

/**
 * Check if the current environment is production
 * Works in both browser and server environments
 */
export function isProduction(): boolean {
  return !isDevelopment() && !isStaging();
}

/**
 * Get the current environment name
 */
export function getEnvironment(): Environment {
  if (isStaging()) return "staging";
  if (isDevelopment()) return "development";
  return "production";
}

/**
 * Get a display-friendly environment name
 */
export function getEnvironmentDisplayName(): string {
  const env = getEnvironment();
  return env.charAt(0).toUpperCase() + env.slice(1);
}
